import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import BaseApi from "../../utils/BaseApi";

// IMPORTANT: Use the CDN worker to avoid Webpack/Vite bundling headaches.
// Ensure this version matches the pdfjs-dist version installed in package.json
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;


async function callVoiceRecognitionAPI(base64Audio, extension, room) {
    const api = new BaseApi();
    const url = `room/${room}/voice/`;

    const data = {
        audio: base64Audio,
        extension: extension
    };

    try {
        const response = await api.post(url, data);
        if (typeof response.data === 'string') {
            return response.data;
        }
        return response.data;
    } catch (e) {
        console.error("Voice recognition error:", e);
        return "[Ошибка распознавания аудио]";
    }
}

/**
 * Extracts text from a File object
 * @param {File} file
 * @returns {Promise<string>}
 */
export async function extractTextFromFile(file, room, snackbarCallback) {
    const fileName = file.name.toLowerCase();
    const ext = fileName.split('.').pop();

    if (fileName.endsWith('.pdf')) {
        return await parsePdfWithAiVision(file, room, snackbarCallback);
    } else if (fileName.endsWith('.docx')) {
        return await parseDocx(file);
    } else if (['.jpg', '.jpeg', '.png', '.webp', '.bmp', '.gif'].some(e => fileName.endsWith(e))) {
        return await parseImage(file, room, snackbarCallback);
    } else if (['wav', 'mp3', 'webm', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) {
        return await extractAudio(file, room, snackbarCallback);
    } else {
        throw new Error('Unsupported file format. Please use PDF, DOCX, images, or audio (wav, mp3, webm).');
    }
}

async function extractAudio(file, room, snackbarCallback) {
    snackbarCallback(true, `Распознавание аудио ${file.name}...`);
    
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async () => {
            try {
                // Убираем префикс data:audio/...;base64,
                const base64Audio = reader.result.split(',')[1];
                const ext = file.name.split('.').pop();
                const result = await callVoiceRecognitionAPI(base64Audio, ext, room);
                resolve(result);
            } catch (e) {
                reject(e);
            }
        };
        reader.onerror = () => reject(new Error('Ошибка чтения аудиофайла'));
        reader.readAsDataURL(file);
    });
}

async function parseImage(file, room, snackbarCallback) {
    snackbarCallback(true, `Распознавание изображения ${file.name}...`);

    const imageDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Ошибка чтения файла'));
        reader.readAsDataURL(file);
    });

    return await callYourVisionModelAPI(imageDataUrl, room);
}

async function renderPageToImage(page, targetResolution = 1024) {
    const canvas = document.createElement('canvas');
    canvas.width = targetResolution;
    canvas.height = targetResolution;

    const context = canvas.getContext('2d');

    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, targetResolution, targetResolution);

    const baseViewport = page.getViewport({ scale: 1.0 });

    const scale = Math.min(targetResolution / baseViewport.width, targetResolution / baseViewport.height);
    const scaledViewport = page.getViewport({ scale });

    const offsetX = (targetResolution - scaledViewport.width) / 2;
    const offsetY = (targetResolution - scaledViewport.height) / 2;

    await page.render({
        canvasContext: context,
        viewport: scaledViewport,
        transform: [scale, 0, 0, scale, offsetX, offsetY]
    }).promise;

    return canvas.toDataURL('image/jpeg', 0.85);
}

async function parsePdfWithAiVision(file, room, snackbarCallback) {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    let compiledText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        compiledText += `--- СТРАНИЦА ${i} ---\n\n`;

        const textContent = await page.getTextContent();
        const pageText = textContent.items
            .map(item => item.str + (item.hasEOL ? '\n' : ' '))
            .join('')
            .trim();

        const isScannedPage = pageText.length < 50; // Порог можно настроить

        if (isScannedPage) {

            snackbarCallback(
                true,
                `Страница ${i} из ${pdf.numPages} похожа на скан. Рендерим целиком...`
            )

            try {
                const pageImageDataUrl = await renderPageToImage(page);
                const aiDescription = await callYourVisionModelAPI(pageImageDataUrl, room);
                compiledText += `[Распознанный текст со скана]:\n${aiDescription.trim()}\n\n`;
            } catch (err) {
                console.error(`Ошибка при распознавании скана страницы ${i}:`, err);
                compiledText += `(Не удалось распознать страницу-скан)\n\n`;
            }
        } else {
            compiledText += `[Текст страницы]:\n${pageText}\n\n`;

            const ops = await page.getOperatorList();
            const imagePromises = [];

            for (let j = 0; j < ops.fnArray.length; j++) {
                if (
                    ops.fnArray[j] === pdfjsLib.OPS.paintImageXObject ||
                    ops.fnArray[j] === pdfjsLib.OPS.paintJpegXObject ||
                    ops.fnArray[j] === pdfjsLib.OPS.paintInlineImageXObject
                ) {
                    const imgKey = ops.argsArray[j][0];
                    imagePromises.push(
                        processEmbeddedImage(page, imgKey, i, room)
                            .then(desc => desc ? `${desc.trim()}\n\n` : '')
                            .catch(() => '')
                    );
                }
            }

            if (imagePromises.length > 0) {
                const imageDescriptions = await Promise.all(imagePromises);
                compiledText += `[Описание изображений на странице]:\n${imageDescriptions.join('')}\n`;
            }
        }

        compiledText += `--- КОНЕЦ ${i} СТРАНИЦЫ ---\n\n`;
    }

    return compiledText.trim();
}

async function processEmbeddedImage(page, imgKey, pageNum, room) {
    const img = await page.objs.get(imgKey);
    if (!img || (img.width && img.width < 100)) return null; // Игнорируем мелочь

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (img.bitmap) {
        canvas.width = img.bitmap.width;
        canvas.height = img.bitmap.height;
        ctx.drawImage(img.bitmap, 0, 0);
    } else if (img.data && img.width && img.height && img.data.length === img.width * img.height * 4) {
        canvas.width = img.width;
        canvas.height = img.height;
        const imgData = ctx.createImageData(img.width, img.height);
        imgData.data.set(img.data);
        ctx.putImageData(imgData, 0, 0);
    } else {
        return null;
    }

    const maxSize = 1536;
    if (canvas.width > maxSize || canvas.height > maxSize) {
        const ratio = Math.min(maxSize / canvas.width, maxSize / canvas.height);
        const newCanvas = document.createElement('canvas');
        newCanvas.width = canvas.width * ratio;
        newCanvas.height = canvas.height * ratio;
        newCanvas.getContext('2d').drawImage(canvas, 0, 0, newCanvas.width, newCanvas.height);

        return await callYourVisionModelAPI(newCanvas.toDataURL('image/jpeg', 0.85), room);
    }

    return await callYourVisionModelAPI(canvas.toDataURL('image/jpeg', 0.85), room);
}

async function callYourVisionModelAPI(imageDataUrl, room) {
    const api = new BaseApi();
    const url = `room/${room}/vl/`;

    const data = {
        content: 'Распознай текст, таблицы и преобразуй графики в таблицы на изображении и оформи это по стандарту Markdown',
        image: imageDataUrl
    };

    const response = await api.post(
        url,
        data
    );
    const message = await response.data;
    return message.content;
}

async function parseDocx(file) {
    const arrayBuffer = await file.arrayBuffer();
    // extractRawText gets just the text. Use convertToHtml if you need formatting.
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
}
